import {
  Avatar,
  Button,
  Card,
  Icon,
  Input,
  Select,
  SelectOption,
  Slider,
  Stack,
  Tag,
  TextBody,
  TextCaption,
  Toggle,
  Tooltip,
} from '@pypestream/design-system';
import {
  ComponentProps,
  KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useReducer,
  useRef,
  useState,
} from 'react';

import {
  checkedOf,
  displayValue,
  effectiveSpec,
  humanizeLeaf,
  invalidLeaves,
  isReadOnlyValue,
  renderedFields,
  startingValues,
  toSavedValues,
  valueOf,
  type EffectiveSpec,
} from './field-specs';
import { type SettingsCardHandle, useSettingsForm } from './settings-form';
import { SETTINGS_CSS } from './settings.styles';
import type {
  SelectChoice,
  SettingsCardSpec,
  SettingsGroupView,
  SettingsSource,
  SettingsValues,
} from './types';

export interface SettingsGroupCardCopy {
  /** "Northwind default", "Guest Care project default", "Pypestream default". */
  sourceLabel: (source: SettingsSource | null) => string;
  /** Where the source's value is set; undefined renders the pill as plain text. */
  sourceHref?: (source: SettingsSource | null) => string | undefined;
  /** "Uses the Northwind default. Open it to see or change it there." */
  sourceTooltip?: (source: SettingsSource | null) => string;
  /** Read after the pill's name when it opens a new tab. */
  opensInNewTab: string;
  /** "Reset to Northwind default". */
  resetLabel: (source: SettingsSource | null) => string;
  /** Visually hidden status of an overridden group. */
  overridden: string;
  /** Visually hidden status of a group with an unsaved edit. */
  unsaved: string;
  /** The info button's name: "More about Model". */
  moreAbout: (label: string) => string;
  recommended: string;
  save: string;
  cancel: string;
  /** The empty choice of a select. */
  notSet: string;
  /** Under a field whose text is not its leaf's type. */
  invalidValue: string;
  /** A switched-off slider's readout. */
  off: string;
  /** Names the switch beside a slider apart from the slider: "Turn on Max Retries". */
  turnOn: (label: string) => string;
  /** Under a field this user changed and another editor saved since: "Someone else set this to 5". */
  changedElsewhere: (value: string) => string;
}

export interface SettingsGroupCardProps {
  view: SettingsGroupView;
  title: string;
  /** Beside the title, before the source pill: a status such as "Needs product check-in". */
  titleAddon?: ReactNode;
  description?: string;
  canManage: boolean;
  /** The top of the chain: nothing above it to name, reset to or override. */
  topmost?: boolean;
  /** Inside another card: no card chrome of its own, and an h3 under that card's h2. */
  embedded?: boolean;
  saving?: boolean;
  error?: string | null;
  spec?: SettingsCardSpec;
  /** Rendered after the fields, inside their grid: `SettingsRow`s in a `rows` card. */
  children?: ReactNode;
  copy: SettingsGroupCardCopy;
  labelFor?: (leaf: string) => string;
  /** Resolve true once the saved view is back; false keeps the draft. */
  onSave: (
    values: SettingsValues,
    lockVersion: number | null
  ) => void | boolean | Promise<boolean>;
  /** Resolve false when the reset did not land: the draft is kept. */
  onReset: (lockVersion: number) => void | boolean | Promise<boolean>;
}

// What the server holds, by value: a parent re-render hands over an equal view
// as a new object, and that must not wipe the user's draft.
const serverStateOf = (view: SettingsGroupView) =>
  JSON.stringify([view.lockVersion, view.values, view.inherited.values]);

interface DraftState {
  of: string;
  /** The server values the draft was started or last rebased from. */
  base: SettingsValues;
  values: SettingsValues;
  /** Leaves this user changed: the only ones a save may carry over a newer view. */
  touched: ReadonlySet<string>;
  /** Touched leaves another editor also changed, with the value they saved. */
  clashes: SettingsValues;
}

const freshDraft = (view: SettingsGroupView): DraftState => {
  const base = startingValues(view);
  return {
    of: serverStateOf(view),
    base,
    values: base,
    touched: new Set(),
    clashes: {},
  };
};

// Compared as the field shows them: a typed "5" and a saved 5 are the same answer.
const sameValue = (a: unknown, b: unknown) =>
  displayValue(a) === displayValue(b);

/**
 * A newer view under a dirty draft, whether another editor saved or a save is
 * landing: untouched leaves follow the server, touched ones keep the user's value.
 */
const rebase = (draft: DraftState, view: SettingsGroupView): DraftState => {
  if (draft.touched.size === 0) return freshDraft(view);
  const base = startingValues(view);
  const values = { ...base };
  const clashes: SettingsValues = {};
  draft.touched.forEach((leaf) => {
    values[leaf] = draft.values[leaf];
    if (sameValue(values[leaf], base[leaf])) return;
    if (leaf in draft.clashes || !sameValue(draft.base[leaf], base[leaf]))
      clashes[leaf] = base[leaf];
  });
  return { ...draft, of: serverStateOf(view), base, values, clashes };
};

type DraftAction =
  | { type: 'set'; values: SettingsValues }
  | { type: 'view'; view: SettingsGroupView; keep: boolean }
  | { type: 'discard' };

const draftReducer = (draft: DraftState, action: DraftAction): DraftState => {
  switch (action.type) {
    case 'set': {
      const touched = new Set(draft.touched);
      const clashes = { ...draft.clashes };
      Object.keys(action.values).forEach((leaf) => {
        touched.add(leaf);
        delete clashes[leaf];
      });
      return {
        ...draft,
        values: { ...draft.values, ...action.values },
        touched,
        clashes,
      };
    }
    case 'view':
      return action.keep ? rebase(draft, action.view) : freshDraft(action.view);
    case 'discard':
      return { ...draft, values: draft.base, touched: new Set(), clashes: {} };
  }
};

// The DS avatar initials every word; the design shows one letter for the level.
const avatarOf = (source: SettingsSource | null) =>
  (source?.name ?? 'Pypestream').trim().split(/\s+/)[0];

const isExternal = (href: string) => /^https?:\/\//.test(href);

/** Escape hides a DS tooltip without moving focus; it has no such key of its own. */
const dismissTooltip = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Escape') return;
  event.currentTarget
    .closest('ps-tooltip')
    ?.dispatchEvent(new Event('mouseleave'));
};

const fits = (choice: SelectChoice, values: SettingsValues) =>
  !choice.when ||
  Object.entries(choice.when).every(([leaf, value]) => values[leaf] === value);

/** A select must show the current value even when no listed choice holds it. */
const choicesWith = (
  field: Extract<EffectiveSpec, { kind: 'select' }>,
  current: string,
  values: SettingsValues
) => {
  const listed = field.options.filter((o) => fits(o, values));
  return current === '' || listed.some((o) => o.value === current)
    ? listed
    : [
        ...listed,
        { value: current, label: field.otherLabel?.(current) ?? current },
      ];
};

/**
 * A read-only row in a `rows` card, after its fields: a value the card shows
 * but does not save.
 */
export function SettingsRow({
  label,
  help,
  children,
}: {
  label: string;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="ui-settings-field ui-settings-field--wide">
      <span className="ui-settings-field__label">{label}</span>
      <span className="ui-settings-field__readout">{children}</span>
      {help && <TextCaption variant="secondary">{help}</TextCaption>}
    </div>
  );
}

export function SettingsGroupCard({
  view,
  title,
  titleAddon,
  description,
  canManage,
  topmost = false,
  embedded = false,
  saving = false,
  error,
  spec,
  children,
  copy,
  labelFor = humanizeLeaf,
  onSave,
  onReset,
}: SettingsGroupCardProps) {
  const form = useSettingsForm();
  const serverState = serverStateOf(view);
  const [state, dispatch] = useReducer(draftReducer, view, freshDraft);
  const { values: draft, clashes } = state;
  const dirty = state.touched.size > 0;
  const [ownSaving, setOwnSaving] = useState(false);
  // A reset's own view arriving mid-flight is not someone else's edit.
  const resetting = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const handle = useRef<SettingsCardHandle>(null);
  // What a switched-off slider comes back to: the value it had before.
  const lastOn = useRef<Record<string, unknown>>({});
  const id = useId();
  const overridden = !topmost && view.values !== null;
  const { source } = view.inherited;

  if (state.of !== serverState)
    dispatch({ type: 'view', view, keep: !resetting.current });

  const fields = renderedFields(view.leaves, spec);
  const invalid = invalidLeaves(view.leaves, draft, spec);
  const busy = saving || ownSaving || Boolean(form?.saving);
  const emphasised = !topmost && (overridden || dirty);

  const set = (leaf: string, value: unknown, also?: SettingsValues) =>
    dispatch({ type: 'set', values: { ...also, [leaf]: value } });

  const discard = () => dispatch({ type: 'discard' });

  const save = async () => {
    const outcome = await onSave(
      toSavedValues(view.leaves, draft, spec),
      view.lockVersion
    );
    const ok = outcome ?? true;
    if (ok) discard();
    return ok;
  };

  const focusFirstInvalid = () => {
    const leaf = invalid[0];
    if (!leaf) return;
    sectionRef.current
      ?.querySelector<HTMLElement>(`[data-leaf="${leaf}"]`)
      ?.focus();
  };

  useImperativeHandle(handle, () => ({
    save,
    cancel: discard,
    focusFirstInvalid,
    focusHeading: () => headingRef.current?.focus(),
    element: () => sectionRef.current,
  }));

  const attach = form?.attach;
  const report = form?.report;
  useEffect(() => attach?.(view.group, handle), [attach, view.group]);
  useEffect(
    () => report?.(view.group, { dirty, invalidCount: invalid.length }),
    [report, view.group, dirty, invalid.length]
  );

  const reset = async () => {
    let ok = true;
    if (view.values !== null && view.lockVersion !== null) {
      resetting.current = true;
      try {
        ok = (await onReset(view.lockVersion)) ?? true;
      } finally {
        resetting.current = false;
      }
    }
    if (ok) discard();
    headingRef.current?.focus();
  };

  const runOwnSave = async () => {
    setOwnSaving(true);
    await save();
    setOwnSaving(false);
  };

  const href = copy.sourceHref?.(source);
  const external = href !== undefined && isExternal(href);
  const pillBody = (
    <>
      <Avatar size="msmall" label={avatarOf(source)} aria-hidden="true" />
      <span>{copy.sourceLabel(source)}</span>
      {href && (
        <Icon
          name={external ? 'external-link' : 'arrow-right'}
          size="xsmall"
          aria-hidden="true"
        />
      )}
      {external && (
        <span className="ui-visually-hidden">{copy.opensInNewTab}</span>
      )}
    </>
  );
  const pill = href ? (
    <a
      className="ui-settings-pill"
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      data-test-id={`settings-source-${view.group}`}
    >
      {pillBody}
    </a>
  ) : (
    <span
      className="ui-settings-pill"
      data-test-id={`settings-source-${view.group}`}
    >
      {pillBody}
    </span>
  );

  const control = (leaf: string, label: string, field: EffectiveSpec) => {
    const value = draft[leaf];
    const disabled = !canManage || busy;
    const hasError = invalid.includes(leaf);
    const presentation = fields.find((f) => f.leaf === leaf);
    const shared = { 'aria-label': label, 'data-leaf': leaf, disabled };

    if (field.kind === 'toggle')
      return (
        <Toggle
          {...shared}
          checked={Boolean(value)}
          onChange={(e) => set(leaf, checkedOf(e))}
        />
      );
    if (field.kind === 'select') {
      const current = displayValue(value);
      return (
        <Select
          {...shared}
          variant="outlined"
          value={current}
          hasError={hasError}
          onChange={(e) => {
            const next = valueOf(e);
            const choice = field.options.find((o) => o.value === next);
            set(leaf, next, choice?.sets);
          }}
        >
          <SelectOption value="">{copy.notSet}</SelectOption>
          {choicesWith(field, current, draft).map((choice) => (
            <SelectOption
              key={choice.value}
              value={choice.value}
              description={choice.description}
            >
              {choice.logo && (
                <Icon
                  slot="prefix"
                  name={choice.logo as ComponentProps<typeof Icon>['name']}
                  size="small"
                />
              )}
              {choice.label}
              {choice.recommended && (
                <Tag slot="suffix" size="small">
                  {copy.recommended}
                </Tag>
              )}
            </SelectOption>
          ))}
        </Select>
      );
    }
    if (field.kind === 'slider' && value === field.off) return null;
    if (field.kind === 'slider')
      return (
        <Slider
          {...shared}
          min={field.min}
          max={field.max}
          step={field.step}
          value={Number(value ?? field.min)}
          showMinMaxLabels={false}
          // @ts-expect-error the DS Slider types declare no input event, though it emits one.
          onInput={(e: Event) => set(leaf, valueOf(e))}
        />
      );
    if (presentation?.locked)
      return (
        <Input
          {...shared}
          variant="outlined"
          suffix={presentation.unit}
          value={presentation.locked.display}
          readonly
        />
      );
    return (
      <Input
        {...shared}
        variant="outlined"
        type={field.kind === 'number' ? 'number' : 'text'}
        min={field.kind === 'number' ? field.min : undefined}
        max={field.kind === 'number' ? field.max : undefined}
        suffix={presentation?.unit}
        placeholder={presentation?.placeholder}
        value={displayValue(value)}
        readonly={isReadOnlyValue(field, value)}
        hasError={hasError}
        helpText={hasError ? copy.invalidValue : undefined}
        onInput={(e) => set(leaf, valueOf(e))}
      />
    );
  };

  const clashText = (leaf: string) =>
    copy.changedElsewhere(displayValue(clashes[leaf]) || copy.notSet);
  const clashAnnouncement = fields
    .filter((f) => f.leaf in clashes)
    .map((f) => `${f.label ?? labelFor(f.leaf)}: ${clashText(f.leaf)}`)
    .join(' ');

  const Heading = embedded ? 'h3' : 'h2';
  const grid = (
    <div
      className={
        spec?.layout === 'rows'
          ? 'ui-settings-grid ui-settings-grid--rows'
          : 'ui-settings-grid'
      }
    >
      {fields.map((presentation) => {
        const { leaf } = presentation;
        const label = presentation.label ?? labelFor(leaf);
        const field = effectiveSpec(leaf, presentation);
        const tipId = `${id}-${leaf}-tip`;

        return (
          <div
            key={leaf}
            className={
              presentation.span === 2
                ? 'ui-settings-field ui-settings-field--wide'
                : 'ui-settings-field'
            }
          >
            <span className="ui-settings-field__label">
              {field.kind === 'slider' && field.off !== undefined && (
                <Toggle
                  aria-label={copy.turnOn(label)}
                  checked={draft[leaf] !== field.off}
                  disabled={!canManage || busy}
                  onChange={(e) => {
                    if (checkedOf(e)) {
                      const back = lastOn.current[leaf];
                      set(leaf, back ?? field.min);
                    } else {
                      lastOn.current[leaf] = draft[leaf];
                      set(leaf, field.off);
                    }
                  }}
                />
              )}
              {label}
              {presentation.sublabel && (
                <span className="ui-settings-field__sublabel">
                  {presentation.sublabel}
                </span>
              )}
              {presentation.tooltip && (
                <Tooltip content={presentation.tooltip} placement="top">
                  <button
                    type="button"
                    className="ui-settings-tip"
                    aria-label={copy.moreAbout(label)}
                    aria-describedby={tipId}
                    onKeyDown={dismissTooltip}
                  >
                    <Icon name="info" size="xsmall" aria-hidden="true" />
                  </button>
                </Tooltip>
              )}
              {presentation.tooltip && (
                <span id={tipId} className="ui-visually-hidden">
                  {presentation.tooltip}
                </span>
              )}
              {presentation.locked?.badge && (
                <Tag size="small">{presentation.locked.badge}</Tag>
              )}
              {field.kind === 'slider' && (
                <output className="ui-settings-field__value">
                  {draft[leaf] === field.off
                    ? copy.off
                    : displayValue(draft[leaf])}
                </output>
              )}
            </span>
            {control(leaf, label, field)}
            {leaf in clashes && (
              <TextCaption
                className="ui-settings-field__clash"
                test-id={`settings-changed-elsewhere-${leaf}`}
              >
                {clashText(leaf)}
              </TextCaption>
            )}
            {presentation.help && (presentation.helpWhen?.(draft) ?? true) && (
              <TextCaption variant="secondary">{presentation.help}</TextCaption>
            )}
          </div>
        );
      })}
      {children}
    </div>
  );
  const content = (
    <>
      <div className="ui-settings-card__head">
        <Heading
          id={`${id}-title`}
          ref={headingRef}
          tabIndex={-1}
          className="ui-settings-card__title"
        >
          {title}
        </Heading>

        {titleAddon}
        {!topmost &&
          (overridden || dirty ? (
            canManage && (
              <Button
                variant="ghost"
                size="small"
                disabled={busy}
                test-id={`settings-reset-${view.group}`}
                onClick={() => void reset()}
              >
                <Icon slot="prefix" name="undo" size="small" />
                {copy.resetLabel(source)}
              </Button>
            )
          ) : copy.sourceTooltip ? (
            <Tooltip content={copy.sourceTooltip(source)} placement="top">
              {pill}
            </Tooltip>
          ) : (
            pill
          ))}
        {(overridden || dirty) && (
          <span className="ui-visually-hidden">
            {dirty ? copy.unsaved : copy.overridden}
          </span>
        )}
      </div>
      {description && (
        <TextBody size="small" variant="secondary">
          {description}
        </TextBody>
      )}

      {spec?.panel ? (
        <div className="ui-settings-panel">
          <div className="ui-settings-panel__head">
            <span className="ui-settings-panel__title">
              {spec.panel.title(draft)}
            </span>
            {spec.panel.meta?.(draft) && (
              <span className="ui-settings-panel__meta">
                {spec.panel.meta(draft)}
              </span>
            )}
          </div>
          {grid}
        </div>
      ) : (
        grid
      )}

      {spec?.help && (
        <TextCaption variant="secondary" className="ui-settings-card__help">
          {spec.help}
        </TextCaption>
      )}

      {error && (
        <TextCaption variant="danger" role="alert">
          {error}
        </TextCaption>
      )}
      <span className="ui-visually-hidden" aria-live="polite">
        {clashAnnouncement}
      </span>

      {!form && dirty && canManage && (
        <Stack justifyContent="end" gutter="small">
          <Button variant="ghost" disabled={busy} onClick={discard}>
            {copy.cancel}
          </Button>
          <Button
            loading={busy}
            disabled={invalid.length > 0}
            test-id={`settings-save-${view.group}`}
            onClick={() => void runOwnSave()}
          >
            {copy.save}
          </Button>
        </Stack>
      )}
    </>
  );

  return (
    <section
      ref={sectionRef}
      className={[
        'ui-settings-card',
        embedded && 'ui-settings-card--embedded',
        emphasised && 'ui-settings-card--over',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-labelledby={`${id}-title`}
      data-test-id={embedded ? `settings-embedded-${view.group}` : undefined}
    >
      <style>{SETTINGS_CSS}</style>
      {embedded ? (
        content
      ) : (
        <Card
          size="large"
          radius="large"
          shadow="none"
          border="default"
          test-id={`settings-group-${view.group}`}
        >
          {content}
        </Card>
      )}
    </section>
  );
}
